import * as Yup from 'yup';
import moment from 'moment';
import { getRequiredText } from '../../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../../global/models/api-model';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { PersonPhoneValues } from '../../../../global/models/person-model';
import { PersonInfoValidationSchema } from '../../../../global/validation-schemas/account-information-form-validation';
import { AddressFields, PersonalInfoFields } from '../../../account-info/models/form-models';
import { InstitutionProps } from '../../models/arc-epi-props';
import { OfficerTypes } from './institution-officers-models';

export enum WithAccessTo {
	map = 'map',
	cic = 'cic',
	bill_payer = 'bill_payer',
}

export interface OfficerAddFormValues extends PersonalInfoFields {
	officerType: OfficerTypes,
	nonEpiscopal: YesNoIndicators;
	withAccessTo: string[];
	// dependent info
	designation_salutation: string;

	// address
	mailing_address_1: string;
	mailing_address_2: string;
	mailing_address_3: string;
	mailing_country: string;
	mailing_city: string;
	mailing_state: string;
	mailing_postal_code: string;

	// phones
	business_phone_country_code: string;
	business_phone_area_code: string;
	business_phone_number: string;
	business_phone_formated: string;
	business_phone_extension: string;

	home_phone_country_code: string;
	home_phone_area_code: string;
	home_phone_number: string;
	home_phone_formated: string;
	home_phone_extension: string;

	mobile_phone_country_code: string;
	mobile_phone_area_code: string;
	mobile_phone_number: string;
	mobile_phone_formated: string;
	mobile_phone_extension: string;

	// email
	personal_email_address: string;
	office_email_address: string;
	matched_person: string;
}

export const OfficerAddMailingAddressRequiredSchema: AddressFields = {
	country: false,
	address1: false,
	address2: false,
	address3: false,
	city: false,
	state: false,
	postalCode: false,
};

export const OfficerAddNonEpiscopalMailingAddressRequiredSchemaSimple: AddressFields = {
	country: true,
	address1: true,
	address2: false,
	address3: false,
	city: true,
	state: false,
	postalCode: false,
};

export const OfficerAddNonEpiscopalMailingAddressRequiredSchemaComplete: AddressFields = {
	country: true,
	address1: true,
	address2: false,
	address3: false,
	city: true,
	state: true,
	postalCode: true,
};

export function getOfficerTypeOptions(seniorOptionText: string, seniorSupplement: string, adminOptionText: string, adminSupplement: string) {
	return [
		{
			label: seniorOptionText,
			value: OfficerTypes.SeniorOfficer,
			description: seniorSupplement,
		}, {
			label: adminOptionText,
			value: OfficerTypes.AdminOfficer,
			description: adminSupplement,
		}];
}

export function getPersonTypeOptions(episcopalOptionText: string, episcopalSupplement: string, nonEpiscopalOptionText: string, nonEpiscopalSupplement: string) {
	return [
		{
			label: episcopalOptionText,
			value: YesNoIndicators.No,
			description: episcopalSupplement,
		}, {
			label: nonEpiscopalOptionText,
			value: YesNoIndicators.Yes,
			description: nonEpiscopalSupplement,
		}];
}

function CicOnlyPersonInfoValidationSchema(labelSetting: LabelSettingProps) {

	return {
		first_name: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.firstNameLabel)),
		last_name: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.lastNameLabel)),

		date_of_birth: Yup.string().nullable()
			.test('valid_format',
				(value, context) => {
					const dobMasked = value?.includes('XX/XX/');
					if (dobMasked) return true;

					if (!value) return context.createError({ message: getRequiredText(labelSetting.requiredFormat, labelSetting.dobLabel) });

					const valueMoment = moment(value);

					if (!valueMoment.isValid()) return context.createError({ message: 'Invalid Format' });

					const nowMoment = moment();

					return valueMoment.diff(nowMoment) >= 0 ? context.createError({ message: labelSetting.dateFutureError || 'Date cannot be in the future' }) : true;
				}),
	};
}


export function officerAddValidationSchema(labelSetting: LabelSettingProps, confirmationPage: boolean, skipSSN: boolean, dobValue: string, personPhoneValues: PersonPhoneValues, institutionProps: InstitutionProps) {
	if (confirmationPage) {
		return Yup.object().shape({
			office_email_address: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.businessEmailLabel))
				.email(labelSetting.emailFormatError),
			personal_email_address: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, labelSetting.personalEmailLabel))
				.email(labelSetting.emailFormatError),
		});
	}

	const personInfoValidation = skipSSN ? CicOnlyPersonInfoValidationSchema(labelSetting) : PersonInfoValidationSchema(labelSetting, dobValue, personPhoneValues, false, skipSSN, false);

	return Yup.object().shape({
		...personInfoValidation,
		withAccessTo: Yup.array().min(1, getRequiredText(labelSetting.requiredFormat, institutionProps.withAccessLabel)).required(getRequiredText(labelSetting.requiredFormat, institutionProps.withAccessLabel)),

		office_email_address: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.businessEmailLabel))
			.email(labelSetting.emailFormatError),
		personal_email_address: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.personalEmailLabel))
			.email(labelSetting.emailFormatError),
	});
}

export function GetOfficerAddNonEpiscopalMailingAddressRequiredSchema(): AddressFields {

	return OfficerAddMailingAddressRequiredSchema;
}